import React, { useState, useEffect } from 'react';
import PageHelmet from '../components/PageHelmet.jsx';
import { Form, Button, Alert, Modal, Dropdown } from 'react-bootstrap';
import { IconButton } from "@mui/material";
import {
    FiFileText,
    FiUsers,
    FiLayout,
    FiSend
} from "react-icons/fi";
import { MdArrowBackIos } from "react-icons/md";
import '../styles/Subscriptions.css';
import CustomDataGrid from '../components/CustomDataGrid.jsx';
import DetailsPanel from '../components/DetailsPanel.jsx';
import {
    existingQuotaExtensionsGridColumns,
    existingQuotaExtensionsRowMapping,
    existingExtraCreditsGridColumns,
    existingExtraCreditsRowMapping,
    existingSubscriptionsGridColumns,
    subscriptionsGridOptionalColumns,
    existingSubscriptionsRowMapping,
    generateGridColumns,
    generateGridRows,
    subscriptionDetailsFieldConfig,
    extraCreditDetailsFieldConfig,
    quotaDetailsFieldConfig,
    generateDetailFields
} from '../utils/PageUtils.js';
import ActionSubscriptionModal from '../components/ActionSubscriptionModal.jsx';
import {
    fetchUsersFromDB,
    fetchSubscriptionPlans,
    fetchSubscriptions,
    fetchExtraCreditsPerCustomer,
    fetchUsageMetricsPerCustomer,
    createSubscription,
    activateSubscription,
    suspendSubscription,
    addExtraCredits,
    updateSubscription,
    extendQuota,
    changePlanAndBillingCycle
} from '../utils/api.js';

const Subscriptions = ({ userRole, bearerToken }) => {
    const [users, setUsers] = useState([]);
    const [plans, setPlans] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const emptySubscriptionFields = {
        customer_id: "",
        user: "",
        subscription_plan_id: "",
        plan: "",
        billing_cycle: "",
        payment_status: "Pending",
        subscription_id: "",
    };
    const [subscriptionFields, setSubscriptionFields] = useState(emptySubscriptionFields);
    const [quotaExtensionFields, setQuotaExtensionFields] = useState({
        subscription_id: "",
        user: "",
        quota: {
            campaigns: 0,
            contacts: 0,
            templates: 0,
            requests: 0,
        },
    })
    const [extraCreditsFields, setExtraCreditsFields] = useState({
        subscription_id: "",
        user: "",
        extra_credit_limit: '',
        price: '',
        payment_status: "Pending"
    })
    const [selectedQuotaExtension, setSelectedQuotaExtension] = useState(null)
    const [selectedQuotaId, setSelectedQuotaId] = useState(null)
    const [selectedExtraCredit, setSelectedExtraCredit] = useState(null)
    const [selectedExtraCreditId, setSelectedExtraCreditId] = useState(null)
    const [selectedTab, setSelectedTab] = useState('quota')
    const [showQuotaExtensionModal, setShowQuotaExtensionModal] = useState(false)
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
    const [subscriptionError, setSubscriptionError] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showActivateModal, setShowActivateModal] = useState(false);
    const [createSubscriptionInfo, setCreateSubscriptionInfo] = useState(null);
    const [createSubscriptionError, setCreateSubscriptionError] = useState(null);
    const [quotaExtensions, setQuotaExtensions] = useState([]);
    const [quotaExtensionsError, setQuotaExtensionsError] = useState(null);
    const [extraCredits, setExtraCredits] = useState([]);
    const [extraCreditsListError, setExtraCreditsListError] = useState(null);
    const quotaFields = [
        { name: "campaigns", label: "Campaigns", icon: <FiLayout /> },
        { name: "contacts", label: "Contacts", icon: <FiUsers /> },
        { name: "templates", label: "Templates", icon: <FiFileText /> },
        { name: "requests", label: "Requests", icon: <FiSend /> }
    ];
    const [showSuspendSubscriptionModal, setShowSuspendSubscriptionModal] = useState(false)
    const [showActivateModalForSuspended, setShowActivateModalForSuspended] = useState(false);
    const [showExtraCreditsModal, setShowExtraCreditsModal] = useState(false);
    const [extraCreditsCompleted, setExtraCreditsCompleted] = useState(false);
    const [extraCreditsError, setExtraCreditsError] = useState(null)
    const [extraCreditsInfo, setExtraCreditsInfo] = useState(null)
    const [showExtraCreditsConfirmModal, setShowExtraCreditsConfirmModal] = useState(false)
    const [refreshSubscriptions, setRefreshSubscriptions] = useState(true);
    const [refreshExtraCredits, setRefreshExtraCredits] = useState(false);
    const [submittingCreateAction, setSubmittingCreateAction] = useState(null);
    const [submittingActivateAction, setSubmittingActivateAction] = useState(null);
    const [isExtendingQuota, setIsExtendingQuota] = useState(false);
    const [isAddingExtraCredits, setIsAddingExtraCredits] = useState(false);
    const [activateModalMode, setActivateModalMode] = useState('activate');
    const [quotaExtensionCompleted, setQuotaExtensionCompleted] = useState(false);
    const [refreshQuotaExtensions, setRefreshQuotaExtensions] = useState(false);
    const excludedQuotaUsageKeys = ['envelopes', 'qr_codes', 'trackables', 'collaterals'];
    const [planChangeMode, setPlanChangeMode] = useState('renew');
    const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
    const [showPlanChangeReviewModal, setShowPlanChangeReviewModal] = useState(false);
    const [planChangeCompleted, setPlanChangeCompleted] = useState(false);
    const [isProcessingPlanChange, setIsProcessingPlanChange] = useState(false);
    const [planChangeError, setPlanChangeError] = useState(null);
    const [planChangeInfo, setPlanChangeInfo] = useState(null);

    const filterQuotaObject = (value, excludeKeys = []) => {
        if (typeof value !== 'object' || value === null) return value || '-';

        const filtered = Object.fromEntries(
            Object.entries(value).filter(([key]) => !excludeKeys.includes(key))
        );

        return Object.keys(filtered).length === 0 ? '-' : filtered;
    };

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const handleMobileBack = () => {
        handleCloseSubscriptionDetails();
    }

    useEffect(() => {
        if (!isMobile) {
            document.body.style.overflow = "auto";
        }
    }, [isMobile]);

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const usersResponse = await fetchUsersFromDB(bearerToken);
                if (usersResponse.status === 200) {
                    const usersData = usersResponse.data;
                    setUsers(usersData.users);
                } else {
                    console.error("Failed to fetch users");
                    throw new Error("Failed to fetch data");
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        }
        fetchUserData();
    }, [])

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const plansResponse = await fetchSubscriptionPlans(bearerToken)
                if (!plansResponse.success) {
                    setSubscriptionError(plansResponse.message || "Unable to fetch Plans");
                    return;
                }
                setPlans(plansResponse?.data?.records || []);
            } catch (error) {
                setSubscriptionError("Error while fetching subscriptions");
            }
        }
        fetchPlans();
    }, [])

    useEffect(() => {
        const getSubscriptions = async () => {
            try {
                const response = await fetchSubscriptions(bearerToken);
                if (!response.success) {
                    setSubscriptionError(response.message || "Unable to fetch subscriptions");
                    return;
                }
                setSubscriptions(response?.data?.records || []);
            } catch (error) {
                setSubscriptionError("Error while fetching subscriptions")
            }
        }
        if (refreshSubscriptions) {
            getSubscriptions();
            setRefreshSubscriptions(false)
        }
    }, [refreshSubscriptions])

    useEffect(() => {
        const customer_id = selectedSubscription?.customer_id;
        if (!customer_id) {
            setQuotaExtensions([]);
            setExtraCredits([]);
            return;
        }

        if (selectedTab === 'quota') {
            const fetchQuotaExtensions = async () => {
                try {
                    const response = await fetchUsageMetricsPerCustomer(bearerToken, customer_id);
                    if (response.success) {
                        const usageMetrics = response.data.records;
                        const sortedRecords = usageMetrics.toSorted((a, b) => new Date(b.start_date) - new Date(a.start_date));

                        setQuotaExtensions(sortedRecords || [])
                    } else {
                        setQuotaExtensionsError(response.message || "Error while fetching quota usage")
                    }
                } catch {
                    setQuotaExtensionsError("Error while fetching quota usage")
                }
            };
            fetchQuotaExtensions();
        } else if (selectedTab === 'extra') {
            const fetchExtraCredits = async () => {
                try {
                    const response = await fetchExtraCreditsPerCustomer(bearerToken, customer_id);
                    if (response.success) {
                        const sortedRecords = (response.data?.records || []).sort(
                            (a, b) => new Date(b.created) - new Date(a.created)
                        );
                        setExtraCredits(sortedRecords)
                    } else {
                        setExtraCreditsListError(response.message || "Error while fetching extra credits")
                    }
                } catch {
                    setExtraCreditsListError("Error while fetching extra credits")
                }
            };
            fetchExtraCredits();
        }
    }, [selectedSubscription, selectedTab, refreshExtraCredits, refreshQuotaExtensions])

    const existingSubscriptionsRows = generateGridRows(subscriptions, existingSubscriptionsRowMapping);
    const exisitingSubscriptionsColumns = generateGridColumns(
        existingSubscriptionsGridColumns,
        "user",
        undefined,
        undefined,
        subscriptionsGridOptionalColumns
    )

    const quotaExtensionsForGrid = quotaExtensions.map((p) => ({
        ...p,
        quota_used: filterQuotaObject(p.quota_used, excludedQuotaUsageKeys),
    }));

    const quotaExtensionRows = generateGridRows(quotaExtensionsForGrid, existingQuotaExtensionsRowMapping);
    const quotaExtensionColumns = generateGridColumns(
        existingQuotaExtensionsGridColumns,
        "usage_metric_id",
        "10px"
    );

    const extraCreditsForGrid = extraCredits.map((credit) => ({
        ...credit,
        price: `$${credit.price.toLocaleString()}`,
    }));
    const extraCreditRows = generateGridRows(extraCreditsForGrid, existingExtraCreditsRowMapping);
    const extraCreditColumns = generateGridColumns(
        existingExtraCreditsGridColumns,
        "extra_credit_id",
        "10px"
    );

    const handleRowClick = (e) => {
        if (selectedRowId === e.row.id) {
            setSelectedRowId(null);
            setSelectedSubscription(null);
            setSelectedQuotaExtension(null);
            setSelectedQuotaId(null);
            setSelectedExtraCredit(null);
            setSelectedExtraCreditId(null);
            return;
        }
        setSelectedRowId(e.row.id);
        const subscription = subscriptions.find((sub) => sub.subscription_id === e.row.subscription_id);
        setSelectedSubscription(subscription);
        setSelectedQuotaExtension(null);
        setSelectedQuotaId(null);
        setSelectedExtraCredit(null);
        setSelectedExtraCreditId(null);
    }

    const handleCloseSubscriptionDetails = () => {
        setSelectedSubscription(null)
        setSelectedRowId(null)
        setSelectedQuotaExtension(null)
        setSelectedQuotaId(null)
        setSelectedExtraCredit(null)
        setSelectedExtraCreditId(null)
    }

    const handleOpenCreateModal = () => {
        setShowCreateModal(true);
        setSubscriptionFields(emptySubscriptionFields);
    }

    const handleCreateSubscription = async (actionType) => {
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);

        const {
            user,
            customer_id,
            subscription_plan_id,
            billing_cycle,
            payment_status
        } = subscriptionFields;

        if (actionType === "Active") {
            if (!customer_id || !subscription_plan_id || !billing_cycle || !payment_status) {
                setCreateSubscriptionError(
                    "Please fill all required fields to activate the subscription."
                );
                return;
            }
            if (payment_status !== "Paid") {
                setCreateSubscriptionError(
                    "You can activate subscription only when payment status is Paid"
                );
                return;
            }
        }

        if (actionType === "Draft") {
            if (!customer_id || !subscription_plan_id || !billing_cycle || !payment_status) {
                setCreateSubscriptionError(
                    "Please fill all fields to save as draft."
                );
                return;
            }
        }

        const selectedPlan = plans.find(plan => plan.plan_id === subscription_plan_id);
        const calculatedPrice = selectedPlan
            ? (billing_cycle === "Annual" ? Number(selectedPlan.annual_price || 0) : Number(selectedPlan.monthly_price || 0))
            : 0;

        const payload = {
            status: actionType,
            customer_id,
            subscription_plan_id,
            billing_cycle,
            payment_status,
            price: calculatedPrice,
        }

        try {
            setSubmittingCreateAction(actionType);

            const response = await createSubscription(bearerToken, payload)
            if (response.success) {
                setCreateSubscriptionInfo(`Subscription created successfully for ${user}`);
                setRefreshSubscriptions(true)
                setSubscriptionFields(emptySubscriptionFields)
                setTimeout(() => {
                    setShowCreateModal(false)
                    setCreateSubscriptionError(null)
                    setCreateSubscriptionInfo(null)
                }, 3000)

            } else {
                setCreateSubscriptionError(response.message || "Failed to create subscription. Please try again later.");
            }

        } catch {
            setCreateSubscriptionError("Failed to create subscription. Please try again later.");
        } finally {
            setSubmittingCreateAction(null);
        }
    }

    const handleCloseSubscriptionModal = () => {
        setShowCreateModal(false)
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
        setSubscriptionFields(emptySubscriptionFields)
    }

    const handleOpenActivateModal = async (subscription, mode = 'activate') => {
        const status = subscription.status;
        if (status === 'Draft') {
            setActivateModalMode(mode);
            setShowActivateModal(true);
        } else if (status === 'Suspended') {
            setShowActivateModalForSuspended(true)
        }
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);

        const matchedUser = users.find((u) => u.customer_id === subscription.customer_id);
        const matchedPlan = plans.find((p) => p.plan_id === subscription.subscription_plan_id);

        setSubscriptionFields({
            customer_id: subscription.customer_id,
            user: matchedUser ? `${matchedUser.user_name} - ${matchedUser.email}` : (subscription.user || ""),
            subscription_plan_id: subscription.subscription_plan_id,
            plan: matchedPlan ? matchedPlan.name : (subscription.plan || ""),
            billing_cycle: subscription.billing_cycle,
            payment_status: subscription.payment_status,
            subscription_id: subscription.subscription_id,
        })
    }

    const handleDraftSubscription = async (actionType) => {
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        const { subscription_id, subscription_plan_id, billing_cycle, payment_status } = subscriptionFields;

        const isEmptyFields = (!subscription_plan_id || subscription_plan_id === "") && (!billing_cycle || billing_cycle === "") && (!payment_status || payment_status === "")
        if (isEmptyFields) {
            setCreateSubscriptionError("Please fill atleast one field to save as draft")
            return;
        }

        const selectedPlan = plans.find(plan => plan.plan_id === subscription_plan_id);
        const calculatedPrice = selectedPlan
            ? (billing_cycle === "Annual" ? Number(selectedPlan.annual_price || 0) : Number(selectedPlan.monthly_price || 0))
            : 0;

        let payload = {
            subscription_plan_id: subscription_plan_id || null,
            billing_cycle: billing_cycle || null,
            payment_status: payment_status || null,
            price: calculatedPrice,
        }

        try {
            setSubmittingActivateAction(actionType);

            const response = await updateSubscription(bearerToken, subscription_id, payload);
            if (response.success) {
                setCreateSubscriptionInfo(response.message);
                setRefreshSubscriptions(true)
                setSubscriptionFields(emptySubscriptionFields)
                setSelectedRowId(null)
                setSelectedSubscription(null)
                setTimeout(() => {
                    handleCloseActivateModal()
                    setCreateSubscriptionError(null)
                    setCreateSubscriptionInfo(null)
                }, 3000)
            } else {
                setCreateSubscriptionError(response.message)
            }
        } catch {
            setCreateSubscriptionError("The operation you attempted has failed. Please contact support")
        }
        finally {
            setSubmittingActivateAction(null);
        }
    }

    const handleActivateSubscription = async (actionType) => {
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        const { subscription_id, customer_id, subscription_plan_id, billing_cycle, payment_status } = subscriptionFields;

        if (!customer_id || !subscription_plan_id || !billing_cycle || !payment_status) {
            setCreateSubscriptionError("All fields are mandatory to activate subscription")
            return;
        }
        if (subscriptionFields.payment_status !== 'Paid') {
            setCreateSubscriptionError("You can activate subscription only when payment status is Paid ")
            return;
        }

        const selectedPlan = plans.find(plan => plan.plan_id === subscription_plan_id);
        const calculatedPrice = selectedPlan
            ? (billing_cycle === "Annual" ? Number(selectedPlan.annual_price || 0) : Number(selectedPlan.monthly_price || 0))
            : 0;

        let payload = {
            action: "activate",
            subscription_plan_id: subscription_plan_id || null,
            billing_cycle: billing_cycle || null,
            payment_status: payment_status || null,
            price: calculatedPrice,
        }

        try {
            setSubmittingActivateAction(actionType);

            const response = await activateSubscription(bearerToken, subscription_id, payload);
            if (response.success) {
                setCreateSubscriptionInfo("Subscription Activated Successfully");
                setRefreshSubscriptions(true)
                setSubscriptionFields(emptySubscriptionFields)
                setTimeout(() => {
                    handleCloseActivateModal()
                    setCreateSubscriptionError(null)
                    setCreateSubscriptionInfo(null)
                }, 3000)
                setSelectedSubscription(response.data)
            } else {
                setCreateSubscriptionError(response.message)
            }
        } catch {
            setCreateSubscriptionError("The operation you attempted has failed. Please contact support")
        }
        finally {
            setSubmittingActivateAction(null);
        }
    }

    const handleCloseActivateModal = () => {
        setShowActivateModal(false);
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        setSubscriptionFields(emptySubscriptionFields)
        setActivateModalMode('activate');
    }

    const handleQuotaExtensionRowClick = (e) => {
        const rowId = e?.row?.id;
        if (selectedQuotaId === rowId) {
            setSelectedQuotaId(null)
            setSelectedQuotaExtension(null)
            return;
        }
        setSelectedQuotaId(rowId);
        const quotaExtension = quotaExtensions.find((quota) =>
            quota.usage_metric_id === e.row.usage_metric_id);
        setSelectedQuotaExtension(quotaExtension)
    }

    const handleExtraCreditRowClick = (e) => {
        const rowId = e?.row?.id;
        if (selectedExtraCreditId === rowId) {
            setSelectedExtraCreditId(null)
            setSelectedExtraCredit(null)
            return;
        }
        setSelectedExtraCreditId(rowId);
        const extraCredit = extraCredits.find((credit) => credit.extra_credit_id === e.row.extra_credit_id);
        setSelectedExtraCredit(extraCredit)
    }

    const handleOpenQuotaExtensionModal = async (subscription) => {
        setShowQuotaExtensionModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null);
        setQuotaExtensionCompleted(false);
        const record = getUsageMetricsForCurrentSubscription()[0];
        const limitObj = record?.quota_limit || record?.quota || {};

        setQuotaExtensionFields({
            user: subscription.user,
            subscription_id: subscription.subscription_id,
            quota: {
                campaigns: Number(limitObj.campaigns) || 0,
                contacts: Number(limitObj.contacts) || 0,
                templates: Number(limitObj.templates) || 0,
                requests: Number(limitObj.requests) || 0,
            }
        });
    }

    const handleQuotaExtension = async (e) => {
        e.preventDefault();
        if (isExtendingQuota || quotaExtensionCompleted) return;

        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null)
        const { subscription_id, quota } = quotaExtensionFields;

        if (!subscription_id) {
            setCreateSubscriptionError("Please update all required fields.");
            return;
        }
        const targetRecord = getUsageMetricsForCurrentSubscription()[0];
        const currentQuotaLimit = targetRecord?.quota_limit || {};

        const changedEntries = Object.entries(quota).filter(([key, value]) => {
            const currentLimit = Number(currentQuotaLimit[key]) || 0;
            return Number(value) !== currentLimit;
        });

        if (changedEntries.length === 0) {
            setCreateSubscriptionError("Please increase at least one quota value to extend.");
            return;
        }
        const invalidFields = changedEntries
            .map(([key, value]) => {
                const currentLimit = Number(currentQuotaLimit[key]) || 0;
                const enteredValue = Number(value);
                if (enteredValue <= currentLimit) {
                    const featureLabel = quotaFields.find((f) => f.name === key)?.label || key;
                    return `${featureLabel} (must be greater than current limit ${currentLimit})`;
                }
                return null;
            })
            .filter(Boolean);

        if (invalidFields.length > 0) {
            setCreateSubscriptionError(
                `Entered value must be greater than the current quota limit for: ${invalidFields.join(", ")}`
            );
            return;
        }
        const extendedQuotaObject = Object.fromEntries(changedEntries);
        try {
            setIsExtendingQuota(true);
            const response = await extendQuota(bearerToken, subscription_id, extendedQuotaObject);
            if (response.success) {

                setCreateSubscriptionInfo("Quota Extended Successfully")
                setQuotaExtensionCompleted(true);
                setRefreshQuotaExtensions(prev => !prev);
                setTimeout(() => {
                    setShowQuotaExtensionModal(false)
                    setCreateSubscriptionError(null)
                    setCreateSubscriptionInfo(null)
                    setQuotaExtensionCompleted(false)
                }, 3000)
            }
            else {
                setCreateSubscriptionError(response.message || "Error while extending quota")
            }
        } catch {
            setCreateSubscriptionError("Error while extending quota")
        } finally {
            setIsExtendingQuota(false);
        }
    }

    const handleCloseQuotaExtensionModal = async () => {
        if (isExtendingQuota || quotaExtensionCompleted) return;
        setShowQuotaExtensionModal(false);
        setCreateSubscriptionError(null);
        setCreateSubscriptionInfo(null);
        setQuotaExtensionFields({
            user: "",
            subscription_id: "",
            quota: { campaigns: 0, contacts: 0, templates: 0, requests: 0 }
        })
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name in quotaExtensionFields.quota) {
            setQuotaExtensionFields((prev) => {
                const newQuota = { ...prev.quota, [name]: Number(value) };
                return {
                    ...prev,
                    quota: newQuota,
                };
            });
        } else {
            setQuotaExtensionFields((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleOpenSuspendModal = async () => {
        setShowSuspendSubscriptionModal(true);
        setCreateSubscriptionError(null)
        setCreateSubscriptionInfo(null)
    }

    const handleSuspendSubscription = async (reason) => {
        try {
            const { subscription_id } = selectedSubscription;

            if (!subscription_id || !reason) {
                return { success: false, message: "Required fields are missing" };
            }

            const payload = {
                action: "suspend",
                reason
            };

            const response = await suspendSubscription(bearerToken, subscription_id, payload);
            if (response.success) {
                setRefreshSubscriptions(true)
                setSelectedSubscription(response.data)

                return { success: true, message: "Subscription suspended successfully" };
            }

            return { success: false, message: response.message };

        } catch {
            return { success: false, message: "The operation you attempted as failed. Please contact support." };
        }
    };

    const handleActivateForSuspend = async () => {
        try {
            const { subscription_id } = selectedSubscription;

            if (!subscription_id) {
                return { success: false, message: "Required fields are missing" };
            }
            const payload = { action: "activate" }

            const response = await activateSubscription(bearerToken, subscription_id, payload);

            if (response.success) {
                setRefreshSubscriptions(true);
                setSelectedSubscription(response.data)
                return { success: true, message: "Subscription activated successfully" };
            }

            return { success: false, message: response.message };

        } catch {
            return { success: false, message: "Something went wrong while activating Subscription" };
        }
    }

    const handleCloseQuotaDetails = async () => {
        setSelectedQuotaExtension(null)
        setSelectedQuotaId(null)
    }

    const handleOpenPlanChangeModal = (mode) => {
        setPlanChangeMode(mode);
        setShowPlanChangeModal(true);
        setPlanChangeError(null);
        setPlanChangeInfo(null);
        setPlanChangeCompleted(false);
        setSubscriptionFields({
            customer_id: selectedSubscription.customer_id,
            user: selectedSubscription.user,
            plan: "No change",
            subscription_plan_id: selectedSubscription.subscription_plan_id,
            billing_cycle: "No change",
            payment_status: "Pending",
            subscription_id: selectedSubscription.subscription_id
        });
    };

    const handleClosePlanChangeModal = () => {
        if (isProcessingPlanChange || planChangeCompleted) return;
        setPlanChangeError(null);
        setPlanChangeInfo(null);
        setSubscriptionFields(emptySubscriptionFields);
        setShowPlanChangeModal(false);
        setPlanChangeCompleted(false);
    };

    const handlePreparePlanChangeReview = () => {
        setPlanChangeError(null);
        setPlanChangeInfo(null);
        setPlanChangeCompleted(false);

        if (planChangeMode === 'upgrade' && !subscriptionFields.plan_change && !subscriptionFields.update_billing_cycle) {
            setPlanChangeError("Please select a plan or billing cycle to upgrade subscription.");
            return;
        }

        if (subscriptionFields.payment_status !== "Paid") {
            setPlanChangeError(`You can ${planChangeMode} subscription only when payment status is Paid`);
            return;
        }

        setShowPlanChangeReviewModal(true);
    };

    const handleClosePlanChangeReviewModal = () => {
        if (isProcessingPlanChange || planChangeCompleted) return;
        setShowPlanChangeReviewModal(false);
        setPlanChangeError(null);
        setPlanChangeInfo(null);
    };

    const handleSubmitPlanChange = async () => {
        if (isProcessingPlanChange || planChangeCompleted) return;
        setPlanChangeError(null);
        setPlanChangeInfo(null);

        const { subscription_plan_id, billing_cycle, subscription_id, payment_status } = subscriptionFields;
        const planDir = getRenewPlanDirection();
        const billingDir = getRenewBillingDirection();

        let action;
        let extraFields = {};

        if (!planDir && !billingDir) {
            if (planChangeMode === 'upgrade') {
                setPlanChangeError("Please select a plan or billing cycle to upgrade subscription.");
                return;
            }
            action = "renew";
        } else if (planDir && !billingDir) {
            action = planChangeMode === 'upgrade' ? "upgrade" : planDir;
            extraFields = { plan_id: subscription_plan_id };
        } else if (!planDir && billingDir) {
            action = "modify_billing_cycle";
            extraFields = { billing_cycle };
        } else if (planDir === "upgrade" && billingDir === "upgrade") {
            action = "upgrade_plan_and_billing_cycle";
            extraFields = { plan_id: subscription_plan_id, billing_cycle };
        } else if (planDir === "downgrade" && billingDir === "downgrade") {
            action = "downgrade_plan_and_billing_cycle";
            extraFields = { plan_id: subscription_plan_id, billing_cycle };
        } else if (planDir === "upgrade" && billingDir === "downgrade") {
            action = "upgrade_plan_and_downgrade_billing_cycle";
            extraFields = { plan_id: subscription_plan_id, billing_cycle };
        } else if (planDir === "downgrade" && billingDir === "upgrade") {
            action = "downgrade_plan_and_upgrade_billing_cycle";
            extraFields = { plan_id: subscription_plan_id, billing_cycle };
        }

        try {
            setIsProcessingPlanChange(true);
            const price = planChangeMode === 'renew'
                ? computeRenewAmount()
                : computeUpgradeAmount().total;

            const payload = { action, price, payment_status, ...extraFields };
            const response = await changePlanAndBillingCycle(bearerToken, subscription_id, payload);

            if (response.success) {
                setRefreshSubscriptions(true);
                setSelectedSubscription(response.data);
                setPlanChangeInfo(planChangeMode === 'renew' ? "Subscription renewed successfully" : "Subscription upgraded successfully");
                setPlanChangeCompleted(true);
                setTimeout(() => {
                    setShowPlanChangeModal(false);
                    setShowPlanChangeReviewModal(false);
                    setSubscriptionFields(emptySubscriptionFields);
                    setPlanChangeError(null);
                    setPlanChangeInfo(null);
                    setPlanChangeCompleted(false);
                }, 3000);
            } else {
                setPlanChangeError(response.message || `Something went wrong while ${planChangeMode === 'renew' ? 'renewing' : 'upgrading'} subscription`);
            }
        } catch {
            setPlanChangeError(`Something went wrong while ${planChangeMode === 'renew' ? 'renewing' : 'upgrading'} subscription`);
        } finally {
            setIsProcessingPlanChange(false);
        }
    };

    const handleOpenExtraCreditsModal = () => {
        setShowExtraCreditsModal(true)
        setExtraCreditsError(null)
        setCreateSubscriptionError(null)
        setExtraCreditsInfo(null)
        setExtraCreditsCompleted(false)
        setExtraCreditsFields({
            user: selectedSubscription?.user,
            subscription_id: selectedSubscription?.subscription_id,
            extra_credit_limit: '',
            price: '',
            payment_status: "Pending"
        })
    }

    const handleCloseExtraCreditsModal = async () => {
        if (isAddingExtraCredits || extraCreditsCompleted) return;
        setExtraCreditsError(null)
        setExtraCreditsInfo(null)
        setCreateSubscriptionError(null)
        setExtraCreditsCompleted(false)
        setExtraCreditsFields({
            user: "",
            subscription_id: "",
            extra_credit_limit: '',
            price: '',
            payment_status: "Pending"
        })
        setShowExtraCreditsModal(false)
    }

    const handleOpenExtraCreditsReviewModal = () => {
        setExtraCreditsError(null);
        setExtraCreditsInfo(null);
        setCreateSubscriptionError(null)
        setExtraCreditsCompleted(false)

        const credits = Number(extraCreditsFields.extra_credit_limit);
        if (!extraCreditsFields.extra_credit_limit || isNaN(credits) || credits < 1 || !Number.isInteger(credits)) {
            setCreateSubscriptionError("Extra credits must be a valid number greater than or equal to 1.");
            return;
        }

        if (extraCreditsFields.payment_status !== "Paid") {
            setCreateSubscriptionError("You can add extra credits only when payment status is Paid");
            return;
        }
        setShowExtraCreditsConfirmModal(true);
    }

    const handleCloseExtraCreditsReviewModal = () => {
        if (isAddingExtraCredits || extraCreditsCompleted) return;
        setShowExtraCreditsConfirmModal(false);
        setExtraCreditsError(null);
        setExtraCreditsInfo(null);
    }

    const handleExtraCredits = async () => {
        if (isAddingExtraCredits || extraCreditsCompleted) return;
        setExtraCreditsError(null);
        setExtraCreditsInfo(null);
        try {
            setIsAddingExtraCredits(true);
            let { subscription_id, extra_credit_limit, price, payment_status } = extraCreditsFields;

            const payload = {
                action: "purchase_extra_credit",
                extra_credit_limit: Number(extra_credit_limit),
                price: Number(price),
                payment_status
            };

            const response = await addExtraCredits(bearerToken, subscription_id, payload);
            if (response.success) {
                setExtraCreditsInfo("Extra credits purchased successfully");

                setSelectedSubscription(response.data);
                setExtraCreditsCompleted(true);
                setRefreshExtraCredits(prev => !prev);
                setTimeout(() => {
                    setShowExtraCreditsModal(false)
                    setShowExtraCreditsConfirmModal(false)
                    setExtraCreditsFields({
                        user: selectedSubscription?.user,
                        subscription_id: selectedSubscription?.subscription_id,
                        extra_credit_limit: '',
                        price: '',
                        payment_status: "Pending"
                    });
                    setExtraCreditsError(null)
                    setExtraCreditsInfo(null)
                    setExtraCreditsCompleted(false)
                }, 3000)

            } else {
                setExtraCreditsError(response.message || "Failed to purchase extra credits");
            }
        } catch {
            setExtraCreditsError("Something went wrong while purchasing extra credits");
        } finally {
            setIsAddingExtraCredits(false);
        }
    };

    const handlePlanChange = (planName, plan_change, planId = null) => {
        const displayPlan = plan_change ? planName : "No change";

        let updatedFields = {
            ...subscriptionFields,
            plan: displayPlan,
            subscription_plan_id: plan_change ? planId : selectedSubscription?.subscription_plan_id,
            plan_change: plan_change,
            upgrade: plan_change,
            payment_status: subscriptionFields.payment_status,
        };

        if (!plan_change) {
            updatedFields.billing_cycle = selectedSubscription?.billing_cycle;
            updatedFields.update_billing_cycle = false;
        }

        setSubscriptionFields(updatedFields);
    };

    const isUpgradeAvailable = () => {
        if (!selectedSubscription || !plans) return false;

        // Subscription records store subscription_plan_id, not a plan name —
        // match against plans by plan_id, not selectedSubscription.plan.
        const currentPlan = getCurrentPlan();

        if (!currentPlan) return false;

        const currentPrice = Number(currentPlan.monthly_price || 0);

        const highestPlan = plans.reduce((acc, p) => {
            const price = Number(p.monthly_price || 0);
            const accPrice = Number(acc.monthly_price || 0);
            return price > accPrice ? p : acc;
        }, plans[0]);

        const highestPrice = Number(highestPlan.monthly_price || 0);

        return selectedSubscription.billing_cycle === "Monthly" || currentPrice < highestPrice;
    };

    const getCurrentUsageMetric = () => {
        if (!selectedSubscription) return null;
        return (
            quotaExtensions.find((q) => q.subscription_id === selectedSubscription.subscription_id) ||
            quotaExtensions[0] ||
            null
        );
    };

    const getQuotaFeatureStats = (featureName) => {
        const records = getUsageMetricsForCurrentSubscription();
        const currentRecord = records[0]
        const limitObj = currentRecord?.quota_limit || {};
        const usedObj = currentRecord?.quota_used || {};
        return {
            limit: Number(limitObj?.[featureName]) || 0,
            used: Number(usedObj?.[featureName]) || 0,
        };
    };

    const getCurrentPlan = () => {
        if (!selectedSubscription || !plans) return null;
        return (
            plans.find(
                (p) => p.plan_id === (selectedSubscription.subscription_plan_id)
            ) || null
        );
    };

    const getRenewPlanDirection = () => {
        if (!subscriptionFields.plan_change) return null;
        const currentPlan = getCurrentPlan();
        const newPlan = plans.find((p) => p.plan_id === subscriptionFields.subscription_plan_id);
        if (!currentPlan || !newPlan) return null;

        const currentPrice = Number(currentPlan.monthly_price || 0);
        const newPrice = Number(newPlan.monthly_price || 0);

        if (newPrice > currentPrice) return "upgrade";
        if (newPrice < currentPrice) return "downgrade";
        return null;
    };

    const getRenewBillingDirection = () => {
        if (!subscriptionFields.update_billing_cycle) return null;
        const current = selectedSubscription?.billing_cycle;
        const next = subscriptionFields.billing_cycle;
        if (!next || next === current || next === "No change") return null;

        if (current === "Monthly" && next === "Annual") return "upgrade";
        if (current === "Annual" && next === "Monthly") return "downgrade";
        return null;
    };

    const getUsageMetricsForCurrentSubscription = () => {
        if (!selectedSubscription) return [];

        const termStart = new Date(selectedSubscription.plan_start_date);
        const termEnd = new Date(selectedSubscription.plan_end_date);

        return quotaExtensions
            .filter((q) => {
                const recordStart = new Date(q.start_date);
                return recordStart >= termStart && recordStart <= termEnd;
            })
            .sort((a, b) => new Date(b.start_date) - new Date(a.start_date)); // latest start_date first, explicit
    };

    const computeRenewAmount = () => {
        const selectedPlan = subscriptionFields.plan_change
            ? plans.find(plan => plan.plan_id === subscriptionFields.subscription_plan_id)
            : getCurrentPlan();

        const bcToUse = subscriptionFields.update_billing_cycle
            ? subscriptionFields.billing_cycle
            : selectedSubscription?.billing_cycle;

        if (!selectedPlan || !bcToUse) return 0;

        return bcToUse === "Annual"
            ? Number(selectedPlan.annual_price) || 0
            : Number(selectedPlan.monthly_price) || 0;
    };

    const computeUpgradeAmount = () => {
        const currentPlan = getCurrentPlan();
        if (!currentPlan) {
            return { total: 0, currentPrice: 0, newPrice: 0, unusedCreditPrice: 0, creditLimit: 0, creditUsed: 0, unusedCredit: 0, elapsedMonths: 0, periodLimit: 0, label: "New Plan Price" };
        }

        const isPlanUpgrade = !!subscriptionFields.upgrade && subscriptionFields.plan && subscriptionFields.plan !== "No change";
        const isBillingUpgrade = !!subscriptionFields.update_billing_cycle && subscriptionFields.billing_cycle && subscriptionFields.billing_cycle !== "No change";

        const currentMonthlyPrice = Number(currentPlan.monthly_price || 0);

        if (!isPlanUpgrade && !isBillingUpgrade) {
            return { total: 0, currentPrice: currentMonthlyPrice, newPrice: 0, unusedCreditPrice: 0, creditLimit: 0, creditUsed: 0, unusedCredit: 0, elapsedMonths: 0, periodLimit: 0, label: "New Plan Price" };
        }

        const newPlan = isPlanUpgrade ? plans.find((p) => p.plan_id === subscriptionFields.subscription_plan_id) : currentPlan;
        if (!newPlan) {
            return { total: 0, currentPrice: currentMonthlyPrice, newPrice: 0, unusedCreditPrice: 0, creditLimit: 0, creditUsed: 0, unusedCredit: 0, elapsedMonths: 0, periodLimit: 0, label: "New Plan Price" };
        }

        // Active + Annual + plan-only upgrade -> credit-based proration across the full 12-month term.
        // Elapsed months are treated as fully used (no rollover), matching current policy.
        if (selectedSubscription?.status === "Active" && selectedSubscription?.billing_cycle === "Annual" && isPlanUpgrade && !isBillingUpgrade) {
            const currentAnnualPrice = Number(currentPlan.annual_price || 0);
            const newAnnualPrice = Number(newPlan.annual_price || 0);

            const records = getUsageMetricsForCurrentSubscription(); // sorted latest start_date first
            const currentRecord = records[0] || {};
            const pastRecords = records.slice(1);

            const monthlyLimit = Number(currentRecord.credit_limit) || 0;
            const currentMonthUsed = Number(currentRecord.credit_used) || 0;
            const elapsedFullMonths = pastRecords.length;

            // Past months: use each month's own credit_limit as fully consumed, regardless of
            // actual usage — it's a closed period. Current month: use actual credit_used from
            // the record with the latest start_date, not derived/assumed.
            const pastMonthsUsed = elapsedFullMonths * monthlyLimit;

            const totalCreditLimit = monthlyLimit * 12;
            const usedCredits = currentMonthUsed + pastMonthsUsed;
            const unusedCredit = Math.max(totalCreditLimit - usedCredits, 0);
            const unusedCreditPrice = totalCreditLimit > 0 ? (unusedCredit / totalCreditLimit) * currentAnnualPrice : 0;
            const total = Math.max(newAnnualPrice - unusedCreditPrice, 0);

            return {
                total,
                currentPrice: currentAnnualPrice,
                newPrice: newAnnualPrice,
                unusedCreditPrice,
                creditLimit: totalCreditLimit,
                creditUsed: usedCredits,
                unusedCredit,
                elapsedMonths: elapsedFullMonths,
                periodLimit: monthlyLimit,
                currentMonthUsed,
                pastMonthsUsed,
                label: "New Plan Price (Annual)",
            };
        }

        // Single-period case (Monthly billing subscriber upgrading plan and/or billing cycle).
        const newPrice = isBillingUpgrade ? Number(newPlan.annual_price || 0) : Number(newPlan.monthly_price || 0);

        const currentRecord = getCurrentUsageMetric();
        const limit = Number(currentRecord?.credit_limit) || 0;
        const used = Number(currentRecord?.credit_used) || 0;

        const unusedCredit = Math.max(limit - used, 0);
        const unusedCreditPrice = limit > 0 ? (unusedCredit / limit) * currentMonthlyPrice : 0;
        const total = Math.max(newPrice - unusedCreditPrice, 0);

        return {
            total,
            currentPrice: currentMonthlyPrice,
            newPrice,
            unusedCreditPrice,
            creditLimit: limit,
            creditUsed: used,
            unusedCredit,
            elapsedMonths: 0,
            periodLimit: limit,
            currentMonthUsed: used,
            pastMonthsUsed: 0,
            label: isBillingUpgrade ? "New Plan Price (Annual)" : "New Plan Price (Monthly)",
        };
    };

    // ===== Details Panels (using shared DetailsPanel component) =====

    const renderSubscriptionDetails = (showCloseIcon = true) => (
        <DetailsPanel
            containerClassName="subscription-details-container"
            heading="SUBSCRIPTION DETAILS"
            showCloseIcon={showCloseIcon}
            onClose={handleCloseSubscriptionDetails}
            headerContent={
                <div className="header-buttons">
                    {selectedSubscription?.status === 'Draft' && (
                        <div className="action-buttons">
                            <Button variant="secondary" onClick={() => handleOpenActivateModal(selectedSubscription, 'edit')}>Edit</Button>
                            <Button className='btn-activate' onClick={() => handleOpenActivateModal(selectedSubscription, 'activate')}>Activate</Button>
                        </div>
                    )}
                    {selectedSubscription?.status === 'Suspended' && (
                        <div className="action-buttons">
                            <Button className='btn-activate' onClick={() => handleOpenActivateModal(selectedSubscription)}>Activate</Button>
                        </div>
                    )}
                    {selectedSubscription?.status === 'Active' && (
                        <div className="action-buttons">
                            <Button className="btn-upgrade" onClick={() => handleOpenPlanChangeModal('upgrade')}>Upgrade</Button>
                            <Button className="btn-extra-credits" onClick={() => handleOpenExtraCreditsModal()}>Extra Credits</Button>
                            <Button className="btn-suspend" onClick={() => handleOpenSuspendModal(selectedSubscription)}>Suspend</Button>
                            <Button className="btn-extend-quota" onClick={() => handleOpenQuotaExtensionModal(selectedSubscription)}>Extend Quota</Button>
                        </div>
                    )}
                    {selectedSubscription?.status === 'Awaiting Renewal' && (
                        <div className="action-buttons">
                            <Button className='btn-renew' onClick={() => handleOpenPlanChangeModal('renew')}>Renew</Button>
                        </div>
                    )}
                </div>
            }
            fields={generateDetailFields(subscriptionDetailsFieldConfig, selectedSubscription)}
        />
    );

    const renderExtraCreditDetails = (showCloseIcon = true) => (
        <DetailsPanel
            containerClassName="quota-details-container"
            heading="EXTRA CREDIT DETAILS"
            showCloseIcon={showCloseIcon}
            onClose={handleCloseQuotaDetails}
            fields={generateDetailFields(extraCreditDetailsFieldConfig, selectedExtraCredit)}
        />
    );

    const renderQuotaDetails = (showCloseIcon = true) => (
        <DetailsPanel
            containerClassName="quota-details-container"
            heading="USAGE METRICS DETAILS"
            showCloseIcon={showCloseIcon}
            onClose={handleCloseQuotaDetails}
            extraContent={
                <>
                    <div className="plan-fields quota-metrics-row">
                        <p className="plan-field-label">Quota Limit:</p>
                        <div style={{ textAlign: "end" }} className="plan-field-value quota-metric-list">
                            {typeof (selectedQuotaExtension?.quota_limit) === 'object' && (selectedQuotaExtension?.quota_limit) !== null && Object.entries(selectedQuotaExtension.quota_limit).map(([k, v]) => (
                                <div className="quota-metric-item" key={k}>{`${k}: ${v}`}</div>
                            ))}
                        </div>
                    </div>
                    <div className="plan-fields quota-metrics-row">
                        <p className="plan-field-label">Quota Used:</p>
                        <div style={{ textAlign: "end" }} className="plan-field-value quota-metric-list">
                            {typeof (selectedQuotaExtension?.quota_used) === 'object' && (selectedQuotaExtension?.quota_used) !== null && Object.entries(selectedQuotaExtension.quota_used)
                                .filter(([key]) => !excludedQuotaUsageKeys.includes(key))
                                .map(([k, v]) => (
                                    <div className="quota-metric-item" key={k}>{`${k}: ${v}`}</div>
                                ))}
                        </div>
                    </div>
                </>
            }
            fields={generateDetailFields(quotaDetailsFieldConfig, selectedQuotaExtension)}
        />
    );

    if (userRole !== "admin") {
        return (
            <div className="user-error">
                <h1>404 - Page Not Found</h1>
                <p>The page you're looking for doesn't exist.</p>
            </div>
        );
    }
    return (
        <div>
            <PageHelmet
                title="Admin Subscriptions"
                description="Admin Subscriptions Page"
                url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/subscribe`}
            />
            <div className={`subscriptions-wrapper ${selectedSubscription ? "has-selected-sub" : ""} ${selectedQuotaExtension ? "has-selected-quota" : ""}`}>
                <div className="subscriptions-page main-row">
                    <div className="left-section sub-list-section">
                        <h2 className="subscriptions-title">Subscriptions</h2>
                        <div className="existing-subscriptions-container">
                            <CustomDataGrid
                                rows={existingSubscriptionsRows}
                                columns={exisitingSubscriptionsColumns}
                                totalRows={existingSubscriptionsRows.length}
                                onRowClick={handleRowClick}
                                selectedRowId={selectedRowId}
                                toolbarProps={{
                                    createAction: {
                                        show: true,
                                        onClick: handleOpenCreateModal,
                                        label: "+ Create"
                                    }
                                }}
                            />

                        </div>
                        {subscriptionError && (
                            <Alert variant="danger" className="mt-3">
                                {subscriptionError}
                            </Alert>
                        )}

                    </div>

                    <div className="right-section sub-details-section">
                        {
                            !isMobile && selectedSubscription && renderSubscriptionDetails(true)
                        }
                        {/* Mobile Overlay 1: Subscription Details + Quota Grid */}
                        {isMobile && selectedSubscription && (
                            <div className="subscription-mobile-screen mobile-open">
                                <div className="mobile-header">
                                    <IconButton size="small" style={{ color: '#fff' }} onClick={handleMobileBack}>
                                        <MdArrowBackIos />
                                    </IconButton>
                                    <div className="mobile-title">{selectedSubscription?.user_name || "Subscription"}</div>
                                </div>
                                <div className="mobile-body">
                                    {renderSubscriptionDetails(false)}
                                    <div className={`quota-panel-header ${selectedTab === 'extra' ? 'extra-active' : ''}`}>
                                        <button
                                            className={selectedTab === 'quota' ? 'subscription-tab active' : 'subscription-tab'}
                                            onClick={() => {
                                                setSelectedTab('quota');
                                                setSelectedExtraCredit(null);
                                                setSelectedExtraCreditId(null);
                                            }}
                                        >
                                            Usage Tracker
                                        </button>
                                        <button
                                            className={selectedTab === 'extra' ? 'subscription-tab active' : 'subscription-tab'}
                                            onClick={() => {
                                                setSelectedTab('extra');
                                                setSelectedQuotaExtension(null);
                                                setSelectedQuotaId(null);
                                            }}
                                        >
                                            Extra Credits
                                        </button>
                                    </div>
                                    {
                                        quotaExtensionsError && selectedTab === 'quota' && (
                                            <div className="alert-danger">
                                                {quotaExtensionsError}
                                            </div>
                                        )
                                    }
                                    {extraCreditsListError && selectedTab === 'extra' && (
                                        <div className="alert-danger">
                                            {extraCreditsListError}
                                        </div>
                                    )}

                                    <div className="mobile-quota-list-section">
                                        <CustomDataGrid
                                            rows={selectedTab === 'quota' ? quotaExtensionRows : extraCreditRows}
                                            columns={selectedTab === 'quota' ? quotaExtensionColumns : extraCreditColumns}
                                            totalRows={selectedTab === 'quota' ? quotaExtensionRows.length : extraCreditRows.length}
                                            onRowClick={selectedTab === 'quota' ? handleQuotaExtensionRowClick : handleExtraCreditRowClick}
                                            selectedRowId={selectedTab === 'quota' ? selectedQuotaId : selectedExtraCreditId}
                                            getRowHeight={() => "auto"}
                                        />
                                    </div>
                                    <div className="mobile-selection-details">
                                        {selectedTab === 'quota' ? (
                                            selectedQuotaExtension && renderQuotaDetails(false)
                                        ) : (
                                            selectedExtraCredit && renderExtraCreditDetails(false)
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
                {!isMobile && selectedSubscription && (
                    <div className="subscriptions-page quota-row">
                        <div className={`quota-panel-header ${selectedTab === 'extra' ? 'extra-active' : ''}`}>
                            <button
                                className={selectedTab === 'quota' ? 'subscription-tab active' : 'subscription-tab'}
                                onClick={() => {
                                    setSelectedTab('quota');
                                    setSelectedExtraCredit(null);
                                    setSelectedExtraCreditId(null);
                                }}
                            >
                                Usage Tracker
                            </button>
                            <button
                                className={selectedTab === 'extra' ? 'subscription-tab active' : 'subscription-tab'}
                                onClick={() => {
                                    setSelectedTab('extra');
                                    setSelectedQuotaExtension(null);
                                    setSelectedQuotaId(null);
                                }}
                            >
                                Extra Credits
                            </button>
                        </div>
                        {
                            quotaExtensionsError && selectedTab === 'quota' && (
                                <div className="alert-danger">
                                    {quotaExtensionsError}
                                </div>
                            )
                        }
                        {extraCreditsListError && selectedTab === 'extra' && (
                            <div className="alert-danger">
                                {extraCreditsListError}
                            </div>
                        )}

                        <div className="left-section quota-list-section">
                            <div className="existing-quota-container">
                                <CustomDataGrid
                                    rows={selectedTab === 'quota' ? quotaExtensionRows : extraCreditRows}
                                    columns={selectedTab === 'quota' ? quotaExtensionColumns : extraCreditColumns}
                                    totalRows={selectedTab === 'quota' ? quotaExtensionRows.length : extraCreditRows.length}
                                    onRowClick={selectedTab === 'quota' ? handleQuotaExtensionRowClick : handleExtraCreditRowClick}
                                    selectedRowId={selectedTab === 'quota' ? selectedQuotaId : selectedExtraCreditId}
                                    getRowHeight={() => "auto"}
                                />
                            </div>
                        </div>

                        <div className="right-section quota-details-section">
                            {selectedTab === 'quota' && selectedQuotaExtension ? (
                                renderQuotaDetails(true)
                            ) : (
                                selectedTab === 'extra' && selectedExtraCredit && renderExtraCreditDetails(true)
                            )}
                        </div>
                    </div>
                )}

            </div>

            {/*Modal to create subscription */}
            <Modal
                show={showCreateModal}
                onHide={handleCloseSubscriptionModal}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className='modal-heading'>New Subscription</Modal.Title>
                </Modal.Header>
                <div className='divider'></div>
                <Modal.Body>
                    <Form onSubmit={(e) => e.preventDefault()}>
                        {/* User Dropdown */}
                        <div>
                            <label className="form-label">Select User <span>*</span></label>
                            <Dropdown className="mb-3 custom-dropdown">
                                <Dropdown.Toggle variant="light" id="dropdown-user" className="d-flex justify-content-between align-items-center">
                                    <span>{subscriptionFields.user || "Select User"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {users.map((user) => (
                                        <Dropdown.Item
                                            key={user.email}
                                            onClick={() => setSubscriptionFields({ ...subscriptionFields, customer_id: user.customer_id, user: `${user.user_name} - ${user.email}` })}
                                        >
                                            {user.user_name} - {user.email}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Plan Dropdown */}
                        <div>
                            <label className="form-label">Select Plan</label>
                            <Dropdown className="mb-3 w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-plan"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.plan || "Select Plan"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    {plans.map((plan) => (
                                        <Dropdown.Item
                                            key={plan.name}
                                            onClick={() => setSubscriptionFields({ ...subscriptionFields, subscription_plan_id: plan.plan_id, plan: plan.name })}
                                        >
                                            {plan.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Billing Cycle */}
                        <div>
                            <label className="form-label">Select Billing Cycle</label>
                            <Dropdown className="w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-billing-cycle"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.billing_cycle || "Select Billing Cycle"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Monthly" })}
                                    >
                                        Monthly
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Annual" })}
                                    >
                                        Annual
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        {/* AMOUNT SECTION */}
                        <div className="amount-section-wrapper">
                            <div className="amount-section-box">
                                <span className="amount-label">Amount Payable</span>
                                <span className="amount-value">
                                    {(() => {
                                        const planToUse = subscriptionFields.plan || selectedSubscription?.plan;
                                        const bcToUse = subscriptionFields.billing_cycle;

                                        // Only show amount if user has selected both plan AND billing cycle
                                        if (!planToUse || !bcToUse) return "$0";

                                        const selectedPlan = plans.find(plan => plan.name === planToUse);
                                        if (!selectedPlan) return "$0";

                                        return bcToUse === "Annual"
                                            ? `$${selectedPlan.annual_price}`
                                            : `$${selectedPlan.monthly_price}`;
                                    })()}
                                </span>
                            </div>
                        </div>

                        {/* Payment Status */}
                        <div className="payment-status-section">
                            <p>Payment Status</p>
                            <Form.Check
                                type="radio"
                                label="Paid"
                                name="paymentStatus"
                                id="payment-paid"
                                value="Paid"
                                checked={subscriptionFields.payment_status === "Paid"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />{' '}
                            <Form.Check
                                type="radio"
                                label="Pending"
                                name="paymentStatus"
                                id="payment-pending"
                                value="Pending"
                                checked={subscriptionFields.payment_status === "Pending"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />
                        </div>

                        {createSubscriptionError && <Alert variant="danger">{createSubscriptionError}</Alert>}
                        {createSubscriptionInfo && <Alert variant="success">{createSubscriptionInfo}</Alert>}

                        {/* Action Buttons */}
                        <div className="modal-action-buttons">
                            <Button
                                style={{ width: "fit-content" }}
                                className='save-draft'
                                variant="outline-secondary"
                                type="button"
                                disabled={!!submittingCreateAction}
                                onClick={(e) => handleCreateSubscription("Draft")}

                            >
                                {submittingCreateAction === "Draft" ? "Saving..." : "Save As Draft"}
                            </Button>

                            <Button
                                style={{ width: "fit-content" }}
                                variant="primary"
                                type="button"
                                className='normal-btn'
                                disabled={!!submittingCreateAction}
                                onClick={(e) => handleCreateSubscription("Active")}
                            >
                                {submittingCreateAction === "Active" ? "Activating..." : "Activate"}
                            </Button>
                        </div>

                        {/* Alerts */}

                    </Form>

                </Modal.Body>

            </Modal>

            {/*Modal to edit and activate draft subscription*/}
            <Modal
                show={showActivateModal}
                onHide={handleCloseActivateModal}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton >
                    <Modal.Title className='modal-heading'>{activateModalMode === 'edit' ? 'Edit Subscription' : 'Activate Subscription'}</Modal.Title>
                </Modal.Header>
                <div className='divider'></div>
                <Modal.Body>

                    <Form onSubmit={(e) => e.preventDefault()}>

                        <div className="mb-3">
                            <label className="form-label">Select User</label>
                            {/* Plan Dropdown */}
                            <Dropdown className="w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-user"
                                    className="d-flex justify-content-between align-items-center"
                                    disabled
                                >
                                    <span>{subscriptionFields.user || "Select User"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {users.map((user) => (
                                        <Dropdown.Item
                                            key={user.email}
                                            onClick={() => {
                                                setSubscriptionFields({ ...subscriptionFields, customer_id: user.customer_id, user: `${user.user_name} - ${user.email}` });
                                            }}
                                        >
                                            {user.user_name} - {user.email}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Select Plan</label>
                            {/* Plan Dropdown */}
                            <Dropdown className=" w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-plan"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.plan || "Select Plan"}</span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {plans.map((plan) => (
                                        <Dropdown.Item
                                            key={plan.name}
                                            onClick={() => {
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    subscription_plan_id: plan.plan_id,
                                                    plan: plan.name
                                                })
                                            }}
                                        >
                                            {plan.name}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* Billing Cycle */}
                        <div >
                            <label className="form-label">Select Billing Cycle</label>
                            <Dropdown className="w-100 custom-dropdown">
                                <Dropdown.Toggle
                                    variant="light"
                                    id="dropdown-billing-cycle"
                                    className="d-flex justify-content-between align-items-center"
                                >
                                    <span>{subscriptionFields.billing_cycle || "Select Billing Cycle"}</span>
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Monthly" })}
                                    >
                                        Monthly
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => setSubscriptionFields({ ...subscriptionFields, billing_cycle: "Annual" })}
                                    >
                                        Annual
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        {/* AMOUNT SECTION */}
                        <div className="amount-section-wrapper">
                            <div className="amount-section-box">
                                <span className="amount-label">Amount Payable</span>
                                <span className="amount-value">
                                    {(() => {
                                        const planToUse = subscriptionFields.plan || selectedSubscription?.plan;
                                        const bcToUse = subscriptionFields.billing_cycle || selectedSubscription?.billing_cycle;

                                        if (!planToUse || !bcToUse) return "$0";

                                        const selectedPlan = plans.find(plan => plan.name === planToUse);
                                        if (!selectedPlan) return "$0";

                                        return bcToUse === "Annual"
                                            ? `$${selectedPlan.annual_price}`
                                            : `$${selectedPlan.monthly_price}`;
                                    })()}
                                </span>
                            </div>
                        </div>

                        {/* Payment Status */}
                        <div className="payment-status-section">
                            <p>Payment Status</p>
                            <Form.Check
                                type="radio"
                                label="Paid"
                                name="paymentStatus"
                                id="payment-paid"
                                value="Paid"
                                checked={subscriptionFields.payment_status === "Paid"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />{' '}
                            <Form.Check
                                type="radio"
                                label="Pending"
                                name="paymentStatus"
                                id="payment-pending"
                                value="Pending"
                                checked={subscriptionFields.payment_status === "Pending"}
                                onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                            />
                        </div>

                        {createSubscriptionError && <Alert variant="danger" className="mt-3">{createSubscriptionError}</Alert>}
                        {createSubscriptionInfo && <Alert variant="success" className="mt-3">{createSubscriptionInfo}</Alert>}

                        <div className="renew-action-buttons">
                            <Button
                                style={{ width: "fit-content" }}
                                variant="outline-secondary"
                                type="button"
                                disabled={!!submittingActivateAction}
                                onClick={handleCloseActivateModal}
                            >
                                Cancel
                            </Button>

                            {activateModalMode === 'edit' ? (
                                <Button
                                    style={{ width: "fit-content" }}
                                    variant="primary"
                                    type="button"
                                    className='normal-btn'
                                    disabled={!!submittingActivateAction}
                                    onClick={() => handleDraftSubscription("Draft")}
                                >
                                    {submittingActivateAction === "Draft" ? "Saving..." : "Save As Draft"}
                                </Button>
                            ) : (
                                <Button
                                    style={{ width: "fit-content" }}
                                    variant="primary"
                                    type="button"
                                    className='normal-btn'
                                    disabled={!!submittingActivateAction}
                                    onClick={() => handleActivateSubscription("activate")}
                                >
                                    {submittingActivateAction === "activate" ? "Activating..." : "Activate"}
                                </Button>
                            )}
                        </div>

                        {/* Alerts */}

                    </Form>

                </Modal.Body>

            </Modal>

            {/* Modal for renew/upgrade subscription */}
            <Modal
                show={showPlanChangeModal}
                onHide={handleClosePlanChangeModal}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className='modal-heading'>
                        {planChangeMode === 'renew' ? 'Renew Subscription' : 'Upgrade Subscription'}
                    </Modal.Title>
                </Modal.Header>

                <div className="divider"></div>

                <Modal.Body>
                    <div className="user-info-section">
                        <div className="user-details">
                            <div className="user-name">{selectedSubscription?.user_name}</div>
                            <div className="user-email">{selectedSubscription?.email}</div>
                        </div>
                        <div className="current-plan-badge">
                            <span className="badge-label">CURRENT</span>
                            <span className="badge-plan">
                                {selectedSubscription?.plan_name} • {selectedSubscription?.billing_cycle}
                            </span>
                        </div>
                    </div>

                    {planChangeMode === 'upgrade' && !isUpgradeAvailable() && !planChangeCompleted ? (
                        <div className="text-center my-4">
                            <p style={{ color: "#0d6efd", fontWeight: 500, fontSize: "1rem", marginBottom: "20px" }}>
                                No upgrade available. You are already on the highest plan and annual billing cycle.
                            </p>
                            <Button variant="primary" onClick={() => setShowPlanChangeModal(false)}>
                                OK
                            </Button>
                        </div>
                    ) : (
                        <Form onSubmit={(e) => e.preventDefault()}>

                            {/* PLAN */}
                            <div className="upgrade-section-card">
                                <p className="form-label">{planChangeMode === 'renew' ? 'Change Plan' : 'Upgrade Plan'}</p>
                                <Dropdown className="w-100 custom-dropdown">
                                    <Dropdown.Toggle
                                        variant="light"
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ minHeight: "38px" }}
                                    >
                                        <span>{subscriptionFields.plan || "Select Plan"}</span>
                                        <i className="ti ti-chevron-down dropdown-toggle-chevron"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item onClick={() => handlePlanChange("No change", false)}>
                                            No change
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        {plans
                                            .filter((plan) => {
                                                if (plan.plan_id === selectedSubscription?.subscription_plan_id) return false;
                                                if (planChangeMode === 'upgrade') {
                                                    const currentPlan = getCurrentPlan();
                                                    return plan.monthly_price > (currentPlan?.monthly_price || 0);
                                                }
                                                return true;
                                            })
                                            .map((plan) => (
                                                <Dropdown.Item
                                                    key={plan.plan_id}
                                                    onClick={() => handlePlanChange(plan.name, true, plan.plan_id)}
                                                >
                                                    {plan.name}
                                                </Dropdown.Item>
                                            ))}
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            {/* BILLING CYCLE */}
                            <div className="upgrade-section-card">
                                <p className="form-label">Update Billing Cycle</p>
                                <Dropdown className="w-100 custom-dropdown">
                                    <Dropdown.Toggle
                                        variant="light"
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ minHeight: "38px" }}
                                    >
                                        {subscriptionFields.billing_cycle || "Select Billing Cycle"}
                                        <i className="ti ti-chevron-down dropdown-toggle-chevron"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                        <Dropdown.Item
                                            active={subscriptionFields.billing_cycle === "No change"}
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "No change",
                                                    update_billing_cycle: false,
                                                })
                                            }
                                        >
                                            No change
                                        </Dropdown.Item>
                                        <Dropdown.Divider />
                                        <Dropdown.Item
                                            active={subscriptionFields.billing_cycle === "Monthly"}
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "Monthly",
                                                    update_billing_cycle: true,
                                                })
                                            }
                                            disabled={planChangeMode === 'upgrade' || selectedSubscription?.billing_cycle === "Monthly"}
                                        >
                                            Monthly
                                        </Dropdown.Item>
                                        <Dropdown.Item
                                            active={subscriptionFields.billing_cycle === "Annual"}
                                            onClick={() =>
                                                setSubscriptionFields({
                                                    ...subscriptionFields,
                                                    billing_cycle: "Annual",
                                                    update_billing_cycle: true,
                                                })
                                            }
                                            disabled={selectedSubscription?.billing_cycle === "Annual"}
                                        >
                                            Annual
                                        </Dropdown.Item>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>

                            {/* AMOUNT — renew shows a flat figure, upgrade shows the proration breakdown */}
                            {planChangeMode === 'renew' ? (
                                <div className="amount-section-wrapper">
                                    <div className="amount-section-box">
                                        <span className="amount-label">Amount Payable</span>
                                        <span className="amount-value">
                                            {(() => {
                                                const selectedPlan = subscriptionFields.plan_change
                                                    ? plans.find(plan => plan.plan_id === subscriptionFields.subscription_plan_id)
                                                    : getCurrentPlan();
                                                const bcToUse = subscriptionFields.update_billing_cycle
                                                    ? subscriptionFields.billing_cycle
                                                    : selectedSubscription?.billing_cycle;
                                                if (!selectedPlan || !bcToUse) return "$0";
                                                return bcToUse === "Annual"
                                                    ? `$${selectedPlan.annual_price}`
                                                    : `$${selectedPlan.monthly_price}`;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: "flex", justifyContent: "center" }}>
                                    <div className="amount-calculation-box" style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "16px", maxWidth: "380px", width: "100%", textAlign: "left" }}>
                                        {(() => {
                                            const {
                                                total, currentPrice, newPrice, unusedCreditPrice,
                                                creditLimit, creditUsed, unusedCredit, elapsedMonths, periodLimit, currentMonthUsed, pastMonthsUsed, label
                                            } = computeUpgradeAmount();
                                            const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;
                                            const hasProration = creditLimit > 0;
                                            const rowGroupStyle = { display: "flex", flexDirection: "column", gap: "2px", padding: "10px 0", borderBottom: "1px solid #eee" };
                                            const labelStyle = { fontSize: "0.85rem", color: "#333", fontWeight: 600 };
                                            const calcStyle = { fontSize: "0.78rem", color: "#999", fontStyle: "italic" };

                                            return (
                                                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                        <span>{label}</span>
                                                        <span>{fmt(newPrice)}</span>
                                                    </div>

                                                    {hasProration && (
                                                        <div style={{ background: "#fff", border: "1px solid #e6e6e6", borderRadius: "10px", padding: "14px 16px", display: "flex", flexDirection: "column" }}>
                                                            <span style={{ display: "inline-block", background: "#f2f4f8", color: "#8a8f99", fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", padding: "4px 10px", borderRadius: "20px", marginBottom: "12px", width: "fit-content" }}>
                                                                CREDIT PRORATION
                                                            </span>
                                                            <div style={rowGroupStyle}>
                                                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                                    <span style={labelStyle}>Total credits</span>
                                                                    <span style={labelStyle}>{creditLimit}</span>
                                                                </div>
                                                                {selectedSubscription?.billing_cycle === 'Annual' && (
                                                                    <div style={calcStyle}>{periodLimit} × 12 months</div>
                                                                )}
                                                            </div>
                                                            <div style={rowGroupStyle}>
                                                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                                    <span style={labelStyle}>Used credits</span>
                                                                    <span style={labelStyle}>{creditUsed}</span>
                                                                </div>
                                                                {elapsedMonths > 0 && (
                                                                    <div style={calcStyle}>
                                                                        Previous months: {pastMonthsUsed} &nbsp;+&nbsp; Current month: {currentMonthUsed}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div style={{ ...rowGroupStyle, borderBottom: "none", paddingBottom: "4px" }}>
                                                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                                    <span style={labelStyle}>Unused credits</span>
                                                                    <span style={labelStyle}>{unusedCredit}</span>
                                                                </div>
                                                                <div style={calcStyle}>{creditLimit} - {creditUsed}</div>
                                                            </div>
                                                        </div>
                                                    )}

                                                    {hasProration && (
                                                        <div style={{ background: "#fff", border: "1px solid #c9dcf5", borderRadius: "10px", padding: "10px 16px" }}>
                                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem", fontWeight: 600, color: "#1a56b0" }}>
                                                                <span>Current Balance</span>
                                                                <span>{fmt(unusedCreditPrice)}</span>
                                                            </div>
                                                            <div style={{ fontSize: "0.78rem", color: "#8a9ec2", fontStyle: "italic", marginTop: "3px" }}>
                                                                {unusedCredit} ÷ {creditLimit} × {fmt(currentPrice)} (current price)
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div style={{ background: "#e4f3e4", border: "1px solid #c3e3c3", borderRadius: "10px", padding: "12px 16px", marginTop: "2px" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                            <div className="summary-total-text">
                                                                <span style={{ fontWeight: 700, fontSize: "1rem", color: "#1b4d1b" }}>Amount Payable</span>
                                                                {hasProration && (
                                                                    <span className="summary-calc">{fmt(newPrice)} - {fmt(unusedCreditPrice)}</span>
                                                                )}
                                                            </div>
                                                            <span style={{ fontWeight: 700, fontSize: "1rem", color: "#1b4d1b" }}>{fmt(total)}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}

                            {/* PAYMENT STATUS */}
                            <div className="payment-box payment-status-row mb-3 mt-3">
                                <p className="payment-status-label">Payment Status</p>
                                <div className="payment-status-options">
                                    <Form.Check
                                        type="radio" label="Paid" name="paymentStatus" value="Paid"
                                        checked={subscriptionFields.payment_status === "Paid"}
                                        onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                                    />
                                    <Form.Check
                                        type="radio" label="Pending" name="paymentStatus" value="Pending"
                                        checked={subscriptionFields.payment_status === "Pending"}
                                        onChange={(e) => setSubscriptionFields({ ...subscriptionFields, payment_status: e.target.value })}
                                    />
                                </div>
                            </div>

                            {planChangeMode === 'renew' && selectedSubscription?.billing_cycle === "Annual" && (
                                <div className="subscription-note mb-3">
                                    <strong>Note:</strong> You can downgrade the plan or change the billing cycle during renewal; once the subscription is active, you must wait until the next renewal to make changes.
                                </div>
                            )}

                            {planChangeError && <Alert variant="danger">{planChangeError}</Alert>}

                            <div className="renew-action-buttons">
                                <Button
                                    variant="outline-secondary"
                                    type="button"
                                    disabled={isProcessingPlanChange || planChangeCompleted}
                                    onClick={handleClosePlanChangeModal}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    type="button"
                                    disabled={isProcessingPlanChange || planChangeCompleted}
                                    onClick={handlePreparePlanChangeReview}
                                >
                                    {planChangeMode === 'renew' ? 'Renew' : 'Upgrade'}
                                </Button>
                            </div>
                        </Form>
                    )}
                </Modal.Body>
            </Modal>

            {/* Modal for quota extensions*/}
            <Modal
                show={showQuotaExtensionModal}
                onHide={handleCloseQuotaExtensionModal}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className='modal-heading'>Extend Quota</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <div className="user-info-section">
                        <div className="user-details">
                            <div className="user-name">
                                {selectedSubscription?.user_name}
                            </div>
                            <div className="user-email" >
                                {selectedSubscription?.email}
                            </div>
                        </div>

                        <div className="current-plan-badge">
                            <span className="badge-label">CURRENT</span>
                            <span className="badge-plan">
                                {selectedSubscription?.plan_name} • {selectedSubscription?.billing_cycle}
                            </span>
                        </div>
                    </div>

                    <Form onSubmit={handleQuotaExtension}>

                        <div className="subscription-features-box">
                            <h5>Quota</h5>

                            <div className="features-grid">
                                {quotaFields.map((feature) => {
                                    const { limit } = getQuotaFeatureStats(feature.name);
                                    const fieldValue = quotaExtensionFields.quota[feature.name] ?? limit;

                                    return (
                                        <div key={feature.name} className="feature-item">
                                            <div className="feature-text">
                                                <div className="feature-label">
                                                    {feature.icon}
                                                    {feature.label}
                                                </div>
                                                <div className="feature-current-value">
                                                    current: {limit.toLocaleString()}
                                                </div>
                                            </div>
                                            <Form.Control
                                                type="number"
                                                name={feature.name}
                                                value={fieldValue}
                                                onChange={handleInputChange}
                                                className="feature-input"
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {createSubscriptionError && <Alert variant="danger" className="mt-3">{createSubscriptionError}</Alert>}
                        {createSubscriptionInfo && <Alert variant="success" className="mt-3">{createSubscriptionInfo}</Alert>}

                        {/* Action Buttons */}
                        <div className="modal-action-buttons">
                            <Button
                                style={{ width: "fit-content" }}
                                className='save-draft'
                                variant="outline-secondary"
                                type="button"
                                onClick={() => handleCloseQuotaExtensionModal()}
                                disabled={isExtendingQuota || quotaExtensionCompleted}
                            >
                                Cancel
                            </Button>

                            <Button
                                style={{ width: "fit-content" }}
                                variant="primary"
                                type="button"
                                className='normal-btn'
                                disabled={isExtendingQuota || quotaExtensionCompleted}
                                onClick={(e) => handleQuotaExtension(e)}
                            >
                                {isExtendingQuota ? "Extending..." : "Extend Quota"}
                            </Button>
                        </div>

                        {/* Alerts */}

                    </Form>

                </Modal.Body>

            </Modal>

            {/* Modal for extra credits*/}
            <Modal
                show={showExtraCreditsModal}
                onHide={() => setShowExtraCreditsModal(false)}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title className='modal-heading'>Add Extra Credits</Modal.Title>
                </Modal.Header>

                <div className="divider"></div>

                <Modal.Body>
                    <Form onSubmit={(e) => {
                        e.preventDefault();
                        handleOpenExtraCreditsReviewModal();
                    }}>

                        {/* User Card */}
                        <div className="user-info-section">
                            <div className="user-details">
                                <div className="user-name">
                                    {selectedSubscription?.user_name}
                                </div>
                                <div className="user-email">{selectedSubscription?.email}</div>
                            </div>

                            <div className="current-plan-badge">
                                <span className="badge-label">CURRENT</span>
                                <span className="badge-plan">

                                    {selectedSubscription?.plan_name} • {selectedSubscription?.billing_cycle}
                                </span>
                            </div>
                        </div>

                        {/* Credit Card */}

                        <div className="extra-credit-card">

                            <h5>Extra Credits</h5>

                            <Form.Control
                                type="number"
                                name="extra_credits"
                                value={extraCreditsFields.extra_credit_limit}
                                placeholder="0"
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const plan = plans.find(
                                        p => p.plan_id === selectedSubscription?.subscription_plan_id
                                    );
                                    const overagePrice = Number(plan?.credit_overage_price || 0);
                                    const credits = value === "" ? "" : Number(value);
                                    const totalPrice = credits === "" || isNaN(credits) ? 0 : credits * overagePrice;

                                    setExtraCreditsFields({
                                        ...extraCreditsFields,
                                        extra_credit_limit: value,
                                        price: totalPrice,
                                    });
                                }}
                                className="credits-input"
                            />
                            {(() => {
                                const plan = plans.find(
                                    p => p.plan_id === selectedSubscription?.subscription_plan_id
                                );

                                const overagePrice = Number(plan?.credit_overage_price || 0);
                                const credits = Number(extraCreditsFields.extra_credit_limit || 0);
                                const total = credits * overagePrice;

                                return (
                                    <>
                                        <div className="price-per-credit-box">
                                            <span>Price per Credit</span>
                                            <span>${overagePrice}</span>
                                        </div>

                                        <div className="amount-payable-box">
                                            <div className="summary-total-text">
                                                <span>Amount Payable</span>
                                                <span className="summary-calc">
                                                    {credits || 0} × ${overagePrice}
                                                </span>
                                            </div>
                                            <span>${total.toFixed(2)}</span>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>



                        {/* PAYMENT STATUS */}
                        <div className="payment-box payment-status-row mb-4">
                            <p className="payment-status-label">Payment Status</p>
                            <div className="payment-status-options">
                                <Form.Check
                                    type="radio"
                                    label="Paid"
                                    name="paymentStatus"
                                    value="Paid"
                                    checked={extraCreditsFields.payment_status === "Paid"}
                                    onChange={(e) =>
                                        setExtraCreditsFields({ ...extraCreditsFields, payment_status: e.target.value })
                                    }
                                />

                                <Form.Check
                                    type="radio"
                                    label="Pending"
                                    name="paymentStatus"
                                    value="Pending"
                                    checked={extraCreditsFields.payment_status === "Pending"}
                                    onChange={(e) =>
                                        setExtraCreditsFields({ ...extraCreditsFields, payment_status: e.target.value })
                                    }
                                />
                            </div>
                        </div>
                        {createSubscriptionError && (
                            <Alert variant="danger">
                                {createSubscriptionError}
                            </Alert>
                        )}

                        {/* Footer */}

                        <div className="renew-action-buttons">

                            <Button
                                style={{ width: "fit-content" }}
                                variant="outline-secondary"
                                disabled={isAddingExtraCredits || extraCreditsCompleted}
                                onClick={handleCloseExtraCreditsModal}
                            >
                                Cancel
                            </Button>

                            <Button
                                style={{ width: "fit-content" }}
                                className="normal-btn"
                                disabled={isAddingExtraCredits || extraCreditsCompleted}
                                onClick={handleOpenExtraCreditsReviewModal}
                            >
                                Add Credits
                            </Button>

                        </div>

                    </Form>
                </Modal.Body>
            </Modal>

            {/* Modal for suspend subscription*/}
            <ActionSubscriptionModal
                show={showSuspendSubscriptionModal}
                onClose={() => setShowSuspendSubscriptionModal(false)}
                actionType="suspend"
                actionTitle="Suspend Subscription"
                actionDescription="The subscription will be temporarily suspended."
                actionButtonLabel="Suspend"
                confirmMessage="Are you sure you want to suspend this subscription?"
                onSubmit={handleSuspendSubscription}
            />

            {/* Modal for activate subscription*/}
            <ActionSubscriptionModal
                show={showActivateModalForSuspended}
                onClose={() => setShowActivateModalForSuspended(false)}
                actionType="activate"
                actionTitle="Activate Subscription"
                actionDescription="This will reactivate the suspended subscription."
                actionButtonLabel="Activate"
                onSubmit={handleActivateForSuspend}
                buttonColor="#2e7d32"
                confirmButtonColor="#2e7d32"
                showReasonField={false}   // hides reason textarea
            />

            {/*Upgrade and Renew review modal*/}
            <Modal
                show={showPlanChangeReviewModal}
                onHide={handleClosePlanChangeReviewModal}
                centered
                backdrop="static"
                size="md"
            >
                <Modal.Header closeButton>
                    <Modal.Title className='modal-heading'>
                        {planChangeMode === 'renew' ? 'Confirm Renewal' : 'Confirm Upgrade'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You're about to {planChangeMode} your subscription with the following changes:</p>

                    <div className="p-3 mb-3" style={{ background: "#f8f9fa", borderRadius: "5px" }}>
                        <p><strong>Plan:</strong> {subscriptionFields.plan_change ? subscriptionFields.plan : "No change"}</p>
                        <p><strong>Billing Cycle:</strong> {subscriptionFields.update_billing_cycle ? subscriptionFields.billing_cycle : "No change"}</p>
                    </div>

                    {subscriptionFields.plan_change && !subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed only the plan and not the billing cycle. Do you want to proceed with {planChangeMode}?
                        </p>
                    )}
                    {subscriptionFields.update_billing_cycle && !subscriptionFields.plan_change && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed only the billing cycle and not the plan. Do you want to proceed with {planChangeMode}?
                        </p>
                    )}
                    {subscriptionFields.plan_change && subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have changed both plan and billing cycle. Do you want to proceed with {planChangeMode}?
                        </p>
                    )}
                    {!subscriptionFields.plan_change && !subscriptionFields.update_billing_cycle && (
                        <p style={{ fontStyle: "italic", color: "#6c757d" }}>
                            You have not changed both plan and billing cycle. Do you want to proceed with {planChangeMode}?
                        </p>
                    )}

                    {planChangeError && <Alert variant="danger">{planChangeError}</Alert>}
                    {planChangeInfo && <Alert variant="success">{planChangeInfo}</Alert>}

                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="secondary" disabled={isProcessingPlanChange || planChangeCompleted} onClick={() => setShowPlanChangeReviewModal(false)}>
                            Go Back & Edit
                        </Button>
                        <Button variant="primary" disabled={isProcessingPlanChange || planChangeCompleted} onClick={handleSubmitPlanChange}>
                            {isProcessingPlanChange ? "Processing..." : `Confirm ${planChangeMode === 'renew' ? 'Renewal' : 'Upgrade'}`}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

            {/*Extra credits confirm modal  */}

            <Modal
                show={showExtraCreditsConfirmModal}
                onHide={handleCloseExtraCreditsReviewModal}
                centered
                backdrop="static"
                size="md"
            >
                <Modal.Header closeButton>
                    <Modal.Title className='modal-heading'>Confirm Extra Credits</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You're about to add extra credits to subscription with the following changes:</p>

                    <div className="p-3 mb-3" style={{ background: "#f8f9fa", borderRadius: "5px" }}>
                        <p>
                            <strong>Total Extra Credits:</strong> {extraCreditsFields.extra_credit_limit}
                        </p>
                        <p>
                            <strong>Total Amount:</strong> ${extraCreditsFields.price}
                        </p>
                    </div>


                    {extraCreditsError && (
                        <Alert variant="danger">
                            {extraCreditsError}
                        </Alert>
                    )}

                    {extraCreditsInfo && (
                        <Alert variant="success">
                            {extraCreditsInfo}
                        </Alert>
                    )}
                    <div className="d-flex justify-content-between">
                        <Button
                            variant="secondary"
                            disabled={isAddingExtraCredits || extraCreditsCompleted}
                            onClick={() => setShowExtraCreditsConfirmModal(false)}
                        >
                            Go Back
                        </Button>
                        <Button
                            variant="primary"
                            disabled={isAddingExtraCredits || extraCreditsCompleted}
                            onClick={() => handleExtraCredits()}
                        >
                            {isAddingExtraCredits ? "Processing..." : "Confirm Credits"}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>

        </div >
    );
};

export default Subscriptions;